'use client';

import Image from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Alignable,
    Button,
    ButtonList,
    ButtonVariant,
    Checkbox,
    Column,
    Columns,
    Divider,
    Icon,
    IconName,
    Input,
    InputStatus,
    Justifiable,
    Link,
    ModalBody,
    ModalFooter,
    RadioTile,
    RadioTiles,
    Spacer,
    SpacerSize,
    Text,
    TextMarkup,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { ContractType } from '@app-types/contract';
import { TradeInQuoteType } from '@app-types/trade-in';

import { Loader } from '@components/loader';

import { TradeInStep } from '@constants/trade-in';

import recommerceIcon from '@assets/logo-recommerce.svg';
import { Iban, IbanValidation } from '@models/iban';
import {
    ReturnSessionTradeInProduct,
    clearSessionTradeInProduct,
    getSessionTradeInStep,
    setSessionTradeInStep,
} from '@product-detail/helpers';
import { StatusState } from '@trilogy-ds/react/lib/objects/facets/Status';

import { TradeInBillLine, TradeInBillLineMobile } from './components';

function IbanInput({
    iban,
    setIban,
    status,
    setStatus,
}: {
    iban: Iban;
    setIban: (iban: Iban) => void;
    status: InputStatus;
    setStatus: (status: InputStatus) => void;
}) {
    const [error, setError] = useState<string>('');

    const handleIbanChange = (value: string) => {
        const newIban = new Iban(value);
        const validation = newIban.getStatusValidation();
        setStatus(validation.status !== IbanValidation.VALID ? InputStatus.ERROR : InputStatus.SUCCESS);
        setError(validation.message);
        setIban(newIban);
    };

    return (
        <Input
            placeholder='Nouvel IBAN'
            help={error}
            status={status}
            iconNameRight={error !== '' ? IconName.EXCLAMATION_CIRCLE : IconName.CHECK_CIRCLE}
            value={iban.getCode()}
            onChange={({ inputValue }) => handleIbanChange(inputValue)}
        />
    );
}

export type MobileTradeInStep3Props = {
    step: TradeInStep;
    setStep: (value: TradeInStep) => void;
    quote: TradeInQuoteType | undefined;
    tradeInBonus: number;
    closeModal: () => void;
    isLoading: boolean;
    contract?: ContractType;
    isRenewal: boolean;
};

export function MobileTradeInStep3({
    step,
    setStep,
    quote,
    tradeInBonus,
    closeModal,
    isLoading,
    contract,
    isRenewal,
}: MobileTradeInStep3Props) {
    const [phoneName, setPhoneName] = useState<string>('');
    const [isOldIban, setIsOldIban] = useState<boolean>(true);
    const initialIban = contract?.iban ? contract.iban : '';
    const [iban, setIban] = useState<Iban>(new Iban(initialIban));
    const [hasAccepted, setHasAccepted] = useState<boolean>(false);
    const [statusIban, setStatusIban] = useState<InputStatus>(InputStatus.DEFAULT);

    const hasBonus = useMemo(() => tradeInBonus > 0, [tradeInBonus]);

    const totalLabel = useMemo(() => (hasBonus ? 'Total' : 'Valeur estimée de votre téléphone'), [hasBonus]);
    const totalIcon = useMemo(() => (hasBonus ? IconName.EQUAL : undefined), [hasBonus]);

    const handleConfirmation = () => {
        closeModal();
        if (quote && quote.estimatedRecoveryAmount >= 10) {
            setSessionTradeInStep(TradeInStep.CONFIRMED, iban.getCode());
            setStep(TradeInStep.CONFIRMED);
        } else {
            clearSessionTradeInProduct();
            setStep(TradeInStep.SEARCH);
        }
    };

    const handleReturn = () => setStep(TradeInStep.FORM);

    const toggleOldIban = () => {
        setIban(new Iban(''));
        setIsOldIban(!isOldIban);
    };

    useEffect(() => {
        const product = getSessionTradeInStep(TradeInStep.SEARCH) as ReturnSessionTradeInProduct[TradeInStep.SEARCH];
        const valueStep = getSessionTradeInStep(
            TradeInStep.CONFIRMATION,
        ) as ReturnSessionTradeInProduct[TradeInStep.CONFIRMATION];

        if (phoneName !== product?.name) {
            setPhoneName(product?.name ?? '');
        }
        if (!iban.getCode() && valueStep?.iban) {
            setIban(new Iban(valueStep.iban));
        }
    }, [step]);

    if (step !== TradeInStep.CONFIRMATION) return null;

    return (
        <>
            <ModalBody>
                {isLoading && (
                    <View flexable justify={Justifiable.JUSTIFIED_CENTER}>
                        <Loader />
                    </View>
                )}
                {!isLoading &&
                    (quote && quote.estimatedRecoveryAmount >= 10 ? (
                        <>
                            <Link role='button' onClick={handleReturn} className='is-flex is-aligned-center'>
                                <Icon name={IconName.ARROW_LEFT} />
                                &nbsp;Retour
                            </Link>
                            <Text>
                                <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    Finalisez la reprise de votre {phoneName}&nbsp;
                                </Text>
                                <Text markup={TextMarkup.SPAN}>(estimation valide jusqu&apos;à 23h59 ce soir).</Text>
                            </Text>
                            <div className='is-hidden-mobile'>
                                <Columns verticalAlign={Alignable.ALIGNED_CENTER} mobile marginless>
                                    {hasBonus && (
                                        <>
                                            <Column size={4}>
                                                <TradeInBillLine
                                                    amount={quote.estimatedRecoveryAmount}
                                                    label='Valeur estimée de votre téléphone'
                                                />
                                            </Column>
                                            <Column size={4}>
                                                <TradeInBillLine
                                                    amount={tradeInBonus}
                                                    label='Bonus reprise'
                                                    icon={IconName.PLUS_CIRCLE}
                                                />
                                            </Column>
                                        </>
                                    )}
                                    <Column size={4}>
                                        <TradeInBillLine
                                            amount={quote.estimatedRecoveryAmount + tradeInBonus}
                                            label={totalLabel}
                                            isOverlined
                                            icon={totalIcon}
                                        />
                                    </Column>
                                </Columns>
                            </div>
                            <div className='is-hidden-tablet'>
                                {hasBonus && (
                                    <>
                                        <TradeInBillLineMobile
                                            amount={quote.estimatedRecoveryAmount}
                                            label='Valeur estimée de votre téléphone'
                                            isFullwidth={false}
                                        />

                                        <TradeInBillLineMobile
                                            amount={tradeInBonus}
                                            label='Bonus reprise'
                                            icon={IconName.PLUS_CIRCLE}
                                            isFullwidth={false}
                                        />
                                    </>
                                )}
                                <TradeInBillLineMobile
                                    amount={quote.estimatedRecoveryAmount + tradeInBonus}
                                    label={totalLabel}
                                    isOverlined
                                    icon={totalIcon}
                                    isFullwidth={!hasBonus}
                                />
                            </div>
                            <Spacer size={SpacerSize.SIX} />
                            {isRenewal && (
                                <>
                                    <Divider />
                                    {contract && contract.iban && (
                                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                            Souhaitez-vous utiliser l&apos;IBAN de votre compte client (
                                            {new Iban(contract.iban).getMaskedIban()}) pour la reprise mobile ?
                                        </Text>
                                    )}
                                    <Text>
                                        Le paiement de votre ancien mobile est réalisé sous 48h par virement bancaire
                                        après contrôle en atelier par notre partenaire&nbsp;
                                        <Image src={recommerceIcon} alt='recommerce' width={100} />
                                    </Text>
                                    <Text>
                                        Vous pouvez utiliser l&apos;IBAN de votre compte client ou en utiliser un
                                        nouveau.
                                    </Text>
                                    <RadioTiles className='grid-radio-list'>
                                        <RadioTile
                                            id='tradeIn-new-iban-yes'
                                            horizontal
                                            label='Oui'
                                            onChange={toggleOldIban}
                                            checked={isOldIban}
                                        />
                                        <RadioTile
                                            id='tradeIn-new-iban-no'
                                            horizontal
                                            label='Non'
                                            onChange={toggleOldIban}
                                            checked={!isOldIban}
                                        />
                                    </RadioTiles>
                                    {!isOldIban && (
                                        <IbanInput
                                            iban={iban}
                                            setIban={setIban}
                                            status={statusIban}
                                            setStatus={setStatusIban}
                                        />
                                    )}
                                </>
                            )}
                            <Divider />
                            <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                Utilisez l&apos;enveloppe préaffranchie, on garantit votre envoi !
                            </Text>
                            <Text>
                                <Text markup={TextMarkup.SPAN}>
                                    Vous recevrez une enveloppe préaffranchie à votre adresse de facturation.&nbsp;
                                </Text>
                                <Image src={recommerceIcon} alt='recommerce' width={100} />
                                <Text markup={TextMarkup.SPAN}>
                                    &nbsp;garantit le transport de votre téléphone et prend en charge les coûts
                                    d&apos;affranchissement.
                                </Text>
                            </Text>
                            <Divider />
                            <View flexable className='is-justified-start-mobile is-justified-end-tablet'>
                                <Checkbox checked={hasAccepted} onChange={() => setHasAccepted(!hasAccepted)}>
                                    <Text>
                                        <Text markup={TextMarkup.SPAN}>J&apos;accepte les&nbsp;</Text>
                                        <Text markup={TextMarkup.SPAN}>
                                            <Link
                                                href='https://assets.recommerce.com/buyback-webgen-app/bouyguestelecom/fr/CGA.Bouygues-Recommerce.pdf'
                                                blank
                                            >
                                                Conditions Générales de Reprise
                                            </Link>
                                        </Text>
                                    </Text>
                                </Checkbox>
                            </View>
                        </>
                    ) : (
                        <>
                            <Link role='button' onClick={handleReturn} className='is-flex is-aligned-center'>
                                <Icon name={IconName.ARROW_LEFT} />
                                &nbsp;Retour
                            </Link>
                            <Alert
                                status={StatusState.INFO}
                                title='Malheureusement, nous ne pouvons pas reprendre votre mobile en ligne car sa valeur estimée est inférieure à 10€.'
                            />
                            <Spacer size={SpacerSize.TWO} />
                            <Text>Cependant, nous pouvons le recycler gratuitement en boutique.</Text>
                            <Text>
                                <Text markup={TextMarkup.SPAN}>Vous pouvez essayer de&nbsp;</Text>
                                <Text markup={TextMarkup.SPAN}>
                                    <Link onClick={() => setStep(TradeInStep.SEARCH)}>
                                        faire estimer un autre mobile&nbsp;
                                    </Link>
                                </Text>
                                <Text markup={TextMarkup.SPAN}>
                                    pour bénéficier du bonus reprise ou finaliser votre achat.
                                </Text>
                            </Text>
                        </>
                    ))}
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
                        Fermer
                    </Button>
                    <Button
                        variant={ButtonVariant.CONVERSION}
                        disabled={
                            (!hasAccepted && quote && quote.estimatedRecoveryAmount >= 10) ||
                            (!isOldIban && statusIban !== InputStatus.SUCCESS)
                        }
                        onClick={handleConfirmation}
                        loading={isLoading}
                    >
                        {quote && quote.estimatedRecoveryAmount >= 10
                            ? 'Finaliser la reprise'
                            : 'Finaliser votre achat'}
                    </Button>
                </ButtonList>
            </ModalFooter>
        </>
    );
}
